import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, XMarkIcon, CheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext';
import { razorpayService } from '../../services/razorpayService';
import firebaseService from '../../services/firebaseService';

const FEATURES = [
  'Interactive 3D page turning',
  'Search by keywords',
  'New topics updated regularly',
  'WhatsApp notifications',
  'Access from any device',
];

const WebAccessModal = ({ isOpen, onClose, onAccessGranted }) => {
  const { user, setPurchaseStatus } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [formData, setFormData] = useState({ whatsappNumber: user?.phone || '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!user) return;
    setIsProcessing(true);
    try {
      const orderResponse = await razorpayService.createOrder(99, 'INR', null, {
        product: 'web_access', userId: user.uid,
        userName: user.name || user.phone, whatsappNumber: formData.whatsappNumber
      });
      if (!orderResponse.success) throw new Error('Failed to create order');

      const paymentResponse = await razorpayService.initializeRazorpayCheckout(
        orderResponse.order.id, 99, 'INR',
        { description: 'Fijoli Web Access Book', customerName: user.name || 'Reader', customerPhone: formData.whatsappNumber, themeColor: '#2563EB' }
      );

      const verificationResponse = await razorpayService.verifyPayment(
        paymentResponse.razorpay_order_id, paymentResponse.razorpay_payment_id, paymentResponse.razorpay_signature
      );

      if (verificationResponse.success) {
        const orderData = {
          userId: user.uid, userName: user.name || user.phone, userEmail: user.email,
          product: 'web_access', amount: 99, currency: 'INR',
          razorpayOrderId: orderResponse.order.id, razorpayPaymentId: paymentResponse.razorpay_payment_id,
          whatsappNumber: formData.whatsappNumber, orderType: 'web_access', status: 'completed', paymentAmount: 99
        };
        await firebaseService.saveOrder(orderData);
        await setPurchaseStatus(true, 'web_access', 99);
        setPaymentSuccess(true);
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      alert('Payment failed: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setPaymentSuccess(false);
    setFormData({ whatsappNumber: user?.phone || '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.97 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className="relative w-full sm:max-w-lg bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden"
          style={{ maxHeight: '92vh', overflowY: 'auto' }}
        >
          {/* Header */}
          <div className="relative px-6 pt-7 pb-5 text-white"
            style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}>
            <button onClick={handleClose}
              className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors focus:outline-none"
              aria-label="Close">
              <XMarkIcon className="h-5 w-5 text-white" />
            </button>
            <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-5 sm:hidden" />
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                <BookOpenIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold leading-tight">Web Access Book</h2>
                <p className="text-xs text-blue-200">Lifetime digital access</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 py-5">
            {!paymentSuccess ? (
              <>
                {/* Pricing */}
                <div className="flex items-center gap-3 mb-5 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-blue-600">₹99</span>
                      <span className="text-base line-through text-gray-400">₹299</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">One-time · Lifetime access</p>
                  </div>
                  <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-100 border border-orange-200 px-2.5 py-1 rounded-full whitespace-nowrap">
                    <SparklesIcon className="h-3 w-3" /> Early Bird
                  </span>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-5">
                  {FEATURES.map(f => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-gray-700">
                      <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                        <CheckIcon className="h-3 w-3 text-green-600" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Form */}
                <form onSubmit={handlePayment} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      WhatsApp Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel" name="whatsappNumber"
                      value={formData.whatsappNumber} onChange={handleInputChange}
                      required placeholder="For order updates & notifications"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-gray-50"
                    />
                    <p className="text-xs text-gray-400 mt-1.5">We'll send new content alerts via WhatsApp</p>
                  </div>

                  <button type="submit" disabled={isProcessing || !user}
                    className={`w-full py-3.5 px-6 rounded-2xl font-semibold text-base transition-all ${isProcessing || !user
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg active:scale-[0.98]'
                      }`}>
                    {isProcessing ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : 'Pay ₹99 & Get Access'}
                  </button>

                  <p className="text-center text-xs text-gray-400">🔒 Secure payment via Razorpay</p>
                </form>
              </>
            ) : (
              /* Success */
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="h-9 w-9 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Payment Successful!</h3>
                <p className="text-sm text-gray-500 mb-5">Welcome to Fijoli! Your access is now active.</p>
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-5 text-left">
                  <p className="text-sm font-medium text-blue-800">📱 WhatsApp updates enabled</p>
                  <p className="text-xs text-blue-500 mt-1">Updates will be sent to {formData.whatsappNumber}</p>
                </div>
                <button
                  onClick={() => { handleClose(); if (onAccessGranted) onAccessGranted(); }}
                  className="w-full py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-semibold text-base transition-colors shadow-md">
                  Start Reading 📖
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default WebAccessModal;
